<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.residuals.residualsBySatelliteChart"
					descriptionKey="stats.residuals.residualsBySatelliteChartTooltip"
				/>
				<TimeSeriesChart v-if="satellitesSelected.length > 0" :isLoading="isLoading" :series="series" :options="chartOptions" :height="500" />
				<EmptyChartFiltersMessage v-else messageKey="stats.residuals.satellitesPlaceholder" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<GnssSelect ref="gnssSelectRef" :onChange="filterSatellitesSelected" />
						<div>
							<div class="flex-wrapper pd-vertical">
								<div class="flex-grow">
									{{ $t('stats.residuals.satellitesLabel') }}
								</div>
								<Button size="small" @click="onSelectAll">
									{{ $t('stats.residuals.mountpointsSelectAllLabel') }}
								</Button>
							</div>
							<Select
								class="width-100"
								v-model:value="satellitesSelected"
								mode="multiple"
								:allowClear="true"
								:options="satellitesOptions"
							/>
						</div>
						<DateRangeChooser v-model="datesRange" @change="fetchStatsItems" />
						<VerticalLayout v-if="isAggregated" class="pd-vertical">
							<LabeledSwitch v-model="showMin" :rightLabel="$t('controls.switches.showMin')" />
							<LabeledSwitch v-model="showMedian" :rightLabel="$t('controls.switches.showMedian')" />
							<LabeledSwitch v-model="showMax" :rightLabel="$t('controls.switches.showMax')" />
						</VerticalLayout>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { Row, Col, Card, Select, Button } from 'ant-design-vue';

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import { useSessionStore } from '@/stores/session';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter } from '@/utils/chartsUtils';

import VerticalLayout from '@/components/common/VerticalLayout';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';
import DateRangeChooser from '@/components/common/DateRangeChooser';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import GnssSelect from '@/components/common/controls/GnssSelect.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';


const getMountpointSeriesKey = (period) => item => {
	if (period > 1 && item.agg_function) {
		return item.mountpoint + '-' + item.agg_function;
	}
	return item.mountpoint;
};
const getValue = item => item.final_value / 1_000_000;

export default {
	components: {
		Row, Col, Card, Select, Button,
		VerticalLayout, GnssSelect, DateRangeChooser, TimeSeriesChart, EmptyChartFiltersMessage,
		LabeledSwitch, StatsChartTitleWithDescription,
	},
	setup() {
		const { apiData, isLoading, doRequest } = Api.useResidualsBySatelliteStatsAggregatedGet();
		const sessionStore = useSessionStore();
		const subNetworksStore = useSubNetworksStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const satellitesSelected = ref([]);
		const gnssSelectRef = ref();
		const showMin = ref(true);
		const showMedian = ref(true);
		const showMax = ref(true);
		const isAggregated = computed(() => apiData.value.period > 1);

		const satellitesOptions = computed(() => Utils.sortArray(apiData.value.satellites || []).filter(satellite => {
			if (gnssSelectRef.value == null || gnssSelectRef.value.selected.length == 0) {
				return true;
			}
			return gnssSelectRef.value.selected.includes(satellite.substring(0, 1));
		}).map(satellite => ({
			value: satellite,
			label: satellite,
		})));

		const fetchStatsItems = () => {
			if (datesRangeStr.value != null) {
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
					mountpoints: satellitesSelected.value,
					sub_network_id: subNetworksStore.subNetworkSelectedId,
					show_min: showMin.value,
					show_median: showMedian.value,
					show_max: showMax.value,
				});
			}
		};

		const filterSatellitesSelected = () => {
			const newSatellitesSelected = satellitesSelected.value.filter(satellite => {
				return gnssSelectRef.value.selected.includes(satellite.substring(0, 1));
			});
			if (newSatellitesSelected.length != satellitesSelected.value.length) {
				satellitesSelected.value = newSatellitesSelected;
			}
		};

		const onSelectAll = () => {
			satellitesSelected.value = satellitesOptions.value.map(item => item.value);
		};

		watch(satellitesSelected, () => {
			fetchStatsItems();
		});
		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			fetchStatsItems();
		});
		watch([showMin, showMedian, showMax], () => {
			fetchStatsItems();
		});

		const series = computed(() => generateSeries({
			items: apiData.value.statsItems.filter(item => item.kind === 'residualsBySatellite'),
			getSeriesKeyFn: getMountpointSeriesKey(apiData.value.period),
			getValueFn: getValue,
			seriesStep: apiData.value.period,
		}).series);

		const chartOptions = computed(() => {
			let options = getBaseOptions('residualsBySatelliteAggregated', series.value, apiData.value.funcCount);
			options.yaxis.title.text = 'metres';
			options.yaxis.decimalsInFloat = 6;
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(6) + ' m');
			return options;
		});

		onMounted(() => {
			fetchStatsItems();
		});

		return {
			isLoading,
			datesRange,
			satellitesOptions,
			satellitesSelected,
			showMin,
			showMedian,
			showMax,
			isAggregated,
			series,
			chartOptions,
			gnssSelectRef,
			filterSatellitesSelected,
			fetchStatsItems,
			onSelectAll,
		};
	},
}
</script>
